import React from 'react';
import { Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import { History, Print, FindInPage } from '@material-ui/icons';
import { CustomChip } from '../sc-components/index';
import { informationColors } from '../../themes';
import { capitalize } from '../sc-utilities';

const StorageStatusCustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        chip_Erased: {
            color: informationColors.success.dark,
            padding: 1,
            fontSize: 12
        },
        chip_NotErased: {
            color: informationColors.warning.dark,
            padding: 1,
            fontSize: 12
        },
        chip_InProgress: {
            color: informationColors.customInProgress.dark,
            padding: 1,
            fontSize: 12
        },
        chip_Error: {
            color: informationColors.error.dark,
            padding: 1,
            margin: 1,
            fontSize: 12
        },
        chip_Warning: {
            color: informationColors.warning.light,
            padding: 1,
            fontSize: 12
        },
        chip_Aborted: {
            color: informationColors.aborted.main,
            padding: 1,
            fontSize: 12
        },
        chip_Started: {
            color: informationColors.started.main,
            padding: 1,
            fontSize: 12
        },
        chip_NA: {
            padding: 1,
            fontSize: 12
        }
    }
    return (
        <Typography key={keyValue} className={className}
            style={possibleStyles[className] ? possibleStyles[className] : possibleStyles['chip_NA']}
        >      {value === 'n/a' ? value : capitalize(value)}
        </Typography>
    );
};

export const getStorageComponent = data => {
    return data.filter(component => component.ComponentType === 'storage');
}
export const getMemoryComponent = data => {
    return data.filter(component => component.ComponentType === 'memory');
}
export const getProcessorComponent = data => {
    return data.filter(component => component.ComponentType === 'cpu');
}
export const getBatteryComponent = data => {
    return data.filter(component => component.ComponentType === 'battery');
}
export const getBaseboardComponent = data => {
    return data.filter(component => component.ComponentType === 'baseboard');
}

export const getComponent = data => {
    let components = { memory: [], storage: [], processor: [], gpu: [], cpu: [], nic: [], baseboard: [], battery: [] }
    for (var component of data) {
        if (!component.Deleted) {
            switch (component.ComponentType) {
                case 'storage':
                    components['storage'].push(component)
                    break;
                case 'memory':
                    components['memory'].push(component);
                    break;
                case 'cpu':
                    components['cpu'].push(component);
                    break;
                case 'battery':
                    components['battery'].push(component);
                    break;
                case 'baseboard':
                    components['baseboard'].push(component);
                    break;
                case 'gpu':
                    components['gpu'].push(component);
                    break;
                case 'nic':
                    components['nic'].push(component);
                    break;
                default:
                    break;
            }
        }
    }
    return components;

}

export const getGraphicComponent = (data, translations) => {
    const date = data.filter(component => component.ComponentType === 'gpu')[0] ? data.filter(component => component.ComponentType === 'gpu')[0] : [];
    return <CustomChip key={`graphic_${date.ComponentID}`}
        value={date && date.ComponentModel ? date.ComponentModel : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
}

export const getNICComponent = (data, translations) => {
    const date = data.filter(component => component.ComponentType === 'nic')[0] ? data.filter(component => component.ComponentType === 'nic')[0] : [];
    return <CustomChip key={`graphic_${date.ComponentID}`}
        value={date && date.ComponentModel ? date.ComponentModel : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
}

export const getMemoryTotalComponent = data => {
    let memoryComponents = getMemoryComponent(data.Components);
    let total = 0;

    for (let memoryComponent of memoryComponents) {
        total += Number(memoryComponent?.MemoryComponents?.[0]?.Size ?? 0);
    }

    if (total < 1073741824) {
        return <CustomChip key={`size_${total}`} value={Math.round(total / 1048576) + ' MB '} />;
    }

    return <CustomChip key={`size_${total}`} value={Math.round(total / 1073741824) + ' GB '} />;
}

export const getErasureStatus = (status, forChip, progress = null, translations) => {
    switch (status) {
        case 'erased':
            return forChip ? 'Erased' : translations && translations['erased'] ? translations['erased'] : 'Erased';
        case 'inprogress':
            return forChip ? 'InProgress' : `${progress ? progress : 0}% ${translations && translations['in_progress'] ? translations['in_progress'] : 'In progress'}`;
        case 'noterased':
            return forChip ? 'NotErased' : translations && translations['not_erased'] ? translations['not_erased'] : 'Not erased';
        case 'warning':
            return forChip ? 'Warning' : translations && translations['warning'] ? translations['warning'] : 'Warning';
        case 'error':
            return forChip ? 'Error' : translations && translations['error'] ? translations['error'] : 'Error';
        case 'aborted':
            return forChip ? 'Aborted' : translations && translations['aborted_by_user'] ? translations['aborted_by_user'] : 'Aborted by user';
        case 'started':
            return forChip ? 'Started' : translations && translations['started'] ? translations['started'] : 'Started';
        default:
            return forChip ? 'NotErased' : translations && translations['not_erased'] ? translations['not_erased'] : 'Not erased';
    }
}

export const getVerificationStatus = (status, forChip, progress = null, translations) => {
    switch (status) {
        case 'verified':
            return forChip ? 'Erased' : translations && translations['verified'] ? translations['verified'] : 'Verified';
        case 'inprogress':
            return forChip ? 'InProgress' : `${progress ? progress : 0}% ${translations && translations['in_progress'] ? translations['in_progress'] : 'In progress'}`;
        case 'notverified':
            return forChip ? 'NotErased' : translations && translations['not_verified'] ? translations['not_verified'] : 'Not verified';
        case 'warning':
            return forChip ? 'Warning' : translations && translations['warning'] ? translations['warning'] : 'Warning';
        case 'error':
            return forChip ? 'Error' : translations && translations['error'] ? translations['error'] : 'Error';
        case 'aborted':
            return forChip ? 'Aborted' : translations && translations['aborted_by_user'] ? translations['aborted_by_user'] : 'Aborted by user';
        case 'started':
            return forChip ? 'Started' : translations && translations['started'] ? translations['started'] : 'Started';
        default:
            return forChip ? 'NotErased' : translations && translations['not_verified'] ? translations['not_verified'] : 'Not verified';
    }
}

export const getMemoryInfo = (data, field, classes, translations) => {
    let memoryComponents = getMemoryComponent(data.Components);
    let total = getMemoryTotalComponent(data);

    switch (field) {
        case 'total':
            return <>
                {total}
            </>;
        case 'vendor':
            return <>
                {Array.isArray(memoryComponents) && memoryComponents.length > 0 ? memoryComponents.splice(0, 3).map(memoryComponent => {
                    return <CustomChip key={`model_${memoryComponent.ComponentID}`} className={classes['chip_text']}
                        value={memoryComponent && memoryComponent.Vendor ? memoryComponent.Vendor : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                })
                    : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
            </>;
        case 'components':
            return <>
                {Array.isArray(memoryComponents) && memoryComponents.length > 0 ? memoryComponents.splice(0, 3).map(memoryComponent => {
                    return <CustomChip key={`serial_${memoryComponent.ComponentID}`} className={classes['chip_text']}
                        value={memoryComponent && memoryComponent.ComponentModel ? memoryComponent.ComponentModel : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                })
                    : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
            </>;
        case 'size':
            return <>
                {Array.isArray(memoryComponents) && memoryComponents.length > 0 ? memoryComponents.splice(0, 3).map(memoryComponent => {
                    let memory = memoryComponent && memoryComponent.MemoryComponents instanceof Array && memoryComponent.MemoryComponents[0] ? memoryComponent.MemoryComponents[0] : {};
                    memory.Size = Number(memory.Size) ? Number(memory.Size) : 0;
                    if (memory.Size < 1073741824) {
                        return <CustomChip key={`size_${memoryComponent.ComponentID}`}
                            value={Math.round(memory.Size / 1048576) + ' MB '} />;
                    }
                    return <CustomChip key={`size_${memoryComponent.ComponentID}`}
                        value={Math.round(memory.Size / 1073741824) + ' GB '} />;
                })
                    : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
            </>;
        default:
            return '[empty]';
    }
};

export const calculateDiskSizeInDecimal = (diskSizeInBytes, translations) => {
    if (isNaN(diskSizeInBytes) || Number(diskSizeInBytes) < 0) {
        return translations['short_not_assigned'] || 'n/a';
    }
    let sizeValue = 0;
    if (diskSizeInBytes < 1000000000) {
        sizeValue = Math.round(diskSizeInBytes / 1000000) + " MB";
    } else if (diskSizeInBytes >= 1000000000 && diskSizeInBytes < 1000000000000) {
        sizeValue = Math.round(diskSizeInBytes / 1000000000) + " GB";
    } else if (diskSizeInBytes >= 1000000000000) {
        sizeValue = Math.round(diskSizeInBytes / 1000000000000) + " TB";
    }
    return sizeValue;
}

export const calculateDiskSizeInBinary = (diskSizeInBytes, translations) => {
    if (isNaN(diskSizeInBytes) || Number(diskSizeInBytes) < 0) {
        return translations['short_not_assigned'] || 'n/a';
    }
    let sizeValue = 0;
    if (diskSizeInBytes < 1073741274) {
        sizeValue = Math.round(diskSizeInBytes / 1048576) + " MiB";
    } else if (diskSizeInBytes >= 1073741274 && diskSizeInBytes < 1099511992568) {
        sizeValue = Math.round(diskSizeInBytes / 1073741824) + " GiB";
    } else if (diskSizeInBytes >= 1099511992568) {
        sizeValue = Math.round(diskSizeInBytes / 1099511627776) + " TiB";
    }
    return sizeValue;
}

export const getStorageInfo = (data, field, classes, translations) => {
    let storageComponents = getStorageComponent(data.Components);
    switch (field) {
        case 'vendor':
            return <>
                {storageComponents.splice(0, 3).map(storageComponent => {
                    return <CustomChip key={`vendor_${storageComponent.ComponentID}`} className={classes['chip_text']}
                        value={storageComponent && storageComponent.Vendor ? storageComponent.Vendor : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                })}
            </>;
        case 'model':
            return <>
                {storageComponents.splice(0, 3).map(storageComponent => {
                    return <CustomChip key={`model_${storageComponent.ComponentID}`} className={classes['chip_text']}
                        value={storageComponent && storageComponent.ComponentModel ? storageComponent.ComponentModel : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                })}

            </>;
        case 'serial':
            return <>
                {storageComponents.splice(0, 3).map(storageComponent => {
                    return <CustomChip key={`serial_${storageComponent.ComponentID}`} className={classes['chip_text']}
                        value={storageComponent && storageComponent.SerialNumber ? storageComponent.SerialNumber : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                })}
            </>;
        case 'status':
            return <>
                {storageComponents.splice(0, 3).map(storageComponent => {
                    let storage = storageComponent && storageComponent.StorageComponents instanceof Array && storageComponent.StorageComponents[0] && storageComponent.StorageComponents[0].Type ? storageComponent.StorageComponents[0] : false;
                    if ((!storage && !storage.Type) || storage.Type.includes('rom')) {
                        return <CustomChip key={`status_${storageComponent.ComponentID}`}
                            className={classes['chip_text']} value={translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                    }
                    return <CustomChip key={`chip_${storageComponent.ComponentID}`}
                        className={classes[`chip_${getErasureStatus(storage.WipeStatus, true)}`]}
                        value={getErasureStatus(storage.WipeStatus, null, storage.WipeProgress, translations)} />
                })}
            </>;
        case 'size':
            return <>
                {storageComponents.splice(0, 3).map(storageComponent => {
                    let storage = storageComponent && storageComponent.StorageComponents instanceof Array && storageComponent.StorageComponents[0] && storageComponent.StorageComponents[0].Type ? storageComponent.StorageComponents[0] : {};
                    storage.Size = Number(storage.Size) ? Number(storage.Size) : 0;
                    return <CustomChip key={`size_${storageComponent.ComponentID}`}
                        value={calculateDiskSizeInDecimal(storage.Size, translations)} />;
                })}
            </>;
        default:
            return '[empty]';
    }
};

export const getProcessorInfo = (data, field, classes, translations) => {
    let processorComponent = getProcessorComponent(data.Components);
    switch (field) {
        case 'name':
            return <>
                {Array.isArray(processorComponent) && processorComponent.length > 0 ? processorComponent.splice(0, 3).map(processor => {
                    return <CustomChip key={`name_${processor.ComponentID}`} className={classes['chip_text']}
                        value={processor && processor.ComponentModel ? processor.ComponentModel : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'} />
                }) : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
            </>;
        case 'speed':
            return <>
                {Array.isArray(processorComponent) && processorComponent.length > 0 ? processorComponent.splice(0, 3).map(processor => {
                    let cpu = processor && processor.ProcessorComponents instanceof Array && processor.ProcessorComponents[0] && processor.ProcessorComponents[0].Clock ? processor.ProcessorComponents[0] : {};
                    cpu.Clock = Number(cpu.Clock) ? Number(cpu.Clock) : 0;
                    return <CustomChip key={`chip_${processor.ComponentID}`} className={classes['chip_text']}
                        value={(cpu.Clock / 1000000000).toFixed(2) + 'GHz'} />
                }) : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
            </>;
        default:
            return translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a';
    }
};

export const getBatteryInfo = (data, field, classes, translations) => {
    let batteryComponents = getBatteryComponent(data.Components);
    return <>
        {Array.isArray(batteryComponents) && batteryComponents.length > 0 ? batteryComponents.splice(0, 3).map(batteryComponent => {
            let battery = batteryComponent.BatteryComponents instanceof Array && batteryComponent.BatteryComponents[0] && batteryComponent.BatteryComponents[0] ? batteryComponent.BatteryComponents[0] : false;
            if (!battery || !battery[field]) {
                return translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a';
            }
            return battery[field];
        }) : translations && translations['short_not_assigned'] ? translations['short_not_assigned'] : 'n/a'}
    </>;
}

export const getPalette = (palettes, type, classes) => {
    if (!palettes || palettes.length < 1) {
        return '[empty]';
    }
    return palettes[type] ?
        <Button key={palettes[type][`${type}ID`]} variant="contained" className={classes.paletteButtons}
            value={palettes[type].ColliID} size='small'>
            {type === 'Colli' ? palettes[type].ColliInventoryID : palettes[type].Name}
        </Button> : '[empty]';
}

export const createActions = props => {
    let component = getComponent(props.productComponent.Components);
    return <div className={props.actions ? props.actions : null}>
        <Tooltip title={props.translations['product_details'] || "Product details"}>
            <IconButton component="div" disabled={props.disabled} aria-label={props.translations['product_details'] || "Product details"} value={props.id}
                onClick={() => props.productComponentHandler(component, props.productComponent)}>
                <FindInPage />
            </IconButton>
        </Tooltip>
        <Tooltip title={props.translations['product_history_view'] || "View product histories"}>
            <IconButton component="div" disabled={props.disabled} aria-label={props.translations['product_history_view'] || "View product histories"} value={props.id}
                onClick={() => props.productHistoriesHandler(props.id)}>
                <History />
            </IconButton>
        </Tooltip>
        <Tooltip title={props.translations['print_label'] || "Print label"}>
            <IconButton component="div" disabled={props.disabled} aria-label={props.translations['print_label'] || "Print label"} value={props.id}
                onClick={() => props.handlePrintLabel(props.id)}>
                <Print />
            </IconButton>
        </Tooltip>
    </div>
};

export const getErasureStatuses = (data, disk, classes, translations) => {
    let storageComponents = getStorageComponent(data.Components);
    let erased = 0;
    let notErased = 0;
    let inProgress = 0;
    let warning = 0;
    let error = 0;
    let aborted = 0;
    let started = 0;

    for (var storageComponent of storageComponents) {
        let storage =
            storageComponent &&
                storageComponent?.StorageComponents instanceof Array &&
                storageComponent?.StorageComponents?.length > 0 &&
                !storageComponent?.StorageComponents?.[0]?.Shredded
                ? storageComponent?.StorageComponents?.[0]
                : false;
        if (!storage) {
            continue;
        }
        switch (storage.WipeStatus) {
            case 'erased':
                erased++;
                break;
            case 'inprogress':
                inProgress++;
                break;
            case 'noterased':
                notErased++;
                break;
            case 'warning':
                warning++;
                break;
            case 'error':
                error++;
                break;
            case 'aborted':
                aborted++;
                break;
            case 'started':
                started++;
                break;
            default:
                notErased++;
                break;
        }
    }
    return disk ? <>
        {Array.isArray(storageComponents) && storageComponents.length > 0 ?
            storageComponents.splice(0, 3).map(storageComponent => {
                let storage =
                    storageComponent &&
                        storageComponent?.StorageComponents instanceof Array &&
                        storageComponent?.StorageComponents?.length > 0 &&
                        !storageComponent?.StorageComponents?.[0]?.Type?.includes('rom') &&
                        !storageComponent?.StorageComponents?.[0]?.Shredded
                        ? storageComponent?.StorageComponents?.[0]
                        : false;

                if ((!storage && !storage.Type) || storage?.Type?.includes('rom')) {
                    return <StorageStatusCustomChip key={`status_${storageComponent?.ComponentID}`}
                        className={classes['chip_text']} value={translations['short_not_assigned'] || 'n/a'} />
                }
                return <StorageStatusCustomChip key={`chip_${storageComponent?.ComponentID}`}
                    className={`chip_${getErasureStatus(storage.WipeStatus, true)}`}
                    value={getErasureStatus(storage.WipeStatus, null, storage.WipeProgress, translations)} />
            })
            : null}
    </> : <>
        {erased > 0 ? <StorageStatusCustomChip key='erasedChipStatus' className={'chip_Erased'} value={`${translations["erased"] || "Erased"}: ${erased}`} /> : null}
        {notErased > 0 ? <StorageStatusCustomChip key='notErasedChipStatus' className={'chip_NotErased'} value={`${translations["not_erased"] || 'Not erased'}: ${notErased}`} /> : null}
        {inProgress > 0 ? <StorageStatusCustomChip key='inProgressChipStatus' className={'chip_InProgress'} value={`${translations["in_progress"] || 'In progress'}: ${inProgress}`} /> : null}
        {error > 0 ? <StorageStatusCustomChip key='errorChipStatus' className={'chip_Error'} value={`${translations["error"] || "Error"} : ${error}`} /> : null}
        {warning > 0 ? <StorageStatusCustomChip key='warningChipStatus' className={'chip_Warning'} value={`${translations["warning"] || "Warning"}: ${warning}`} /> : null}
        {aborted > 0 ? <StorageStatusCustomChip key='abortedChipStatus' className={'chip_Aborted'} value={`${translations["aborted_by_user"] || "Aborted by user"} : ${aborted}`} /> : null}
        {started > 0 ? <StorageStatusCustomChip key='startedChipStatus' className={'chip_Started'} value={`${translations["started"] || "Started"} : ${started}`} /> : null}
        {!erased && !notErased && !inProgress && !error && !warning && !aborted && !started ? <StorageStatusCustomChip key='noStoragesChipStatus' className={'chip_InProgress'} value={`${translations["no_storages"] || "No storages"}`} /> : null}

    </>
}

export const getVerificationStatuses = (data, disk, classes, translations) => {
    let storageComponents = getStorageComponent(data.Components);
    let verified = 0;
    let notVerified = 0;
    let inProgress = 0;
    let warning = 0;
    let error = 0;
    let aborted = 0;
    let started = 0;

    for (var storageComponent of storageComponents) {
        let storage =
            storageComponent &&
                storageComponent?.StorageComponents instanceof Array &&
                storageComponent?.StorageComponents?.length > 0 &&
                !storageComponent?.StorageComponents?.[0]?.Shredded
                ? storageComponent?.StorageComponents?.[0]
                : false;
        if (!storage) {
            continue;
        }
        switch (storage.VerificationStatus) {
            case 'verified':
                verified++;
                break;
            case 'inprogress':
                inProgress++;
                break;
            case 'notverified':
                notVerified++;
                break;
            case 'warning':
                warning++;
                break;
            case 'error':
                error++;
                break;
            case 'aborted':
                aborted++;
                break;
            case 'started':
                started++;
                break;
            default:
                notVerified++;
                break;
        }
    }
    return disk ? <>
        {Array.isArray(storageComponents) && storageComponents.length > 0 ?
            storageComponents.splice(0, 3).map(storageComponent => {
                let storage =
                    storageComponent &&
                        storageComponent?.StorageComponents instanceof Array &&
                        storageComponent?.StorageComponents?.length > 0 &&
                        !storageComponent?.StorageComponents?.[0]?.Type?.includes('rom') &&
                        !storageComponent?.StorageComponents?.[0]?.Shredded
                        ? storageComponent?.StorageComponents?.[0]
                        : false;

                if ((!storage && !storage.Type) || storage?.Type?.includes('rom')) {
                    return <StorageStatusCustomChip key={`status_${storageComponent?.ComponentID}`}
                        className={classes['chip_text']} value={translations['short_not_assigned'] || 'n/a'} />
                }
                return <StorageStatusCustomChip key={`chip_${storageComponent?.ComponentID}`}
                    className={`chip_${getVerificationStatus(storage.VerificationStatus, true)}`}
                    value={getVerificationStatus(storage.VerificationStatus, null, storage.VerificationProgress, translations)} />
            })
            : null}
    </> : <>
        {verified > 0 ? <StorageStatusCustomChip key='erasedChipStatus' className={'chip_Erased'} value={`${translations["verified"] || "Verified"}: ${verified}`} /> : null}
        {notVerified > 0 ? <StorageStatusCustomChip key='notErasedChipStatus' className={'chip_NotErased'} value={`${translations["not_verified"] || 'Not verified'}: ${notVerified}`} /> : null}
        {inProgress > 0 ? <StorageStatusCustomChip key='inProgressChipStatus' className={'chip_InProgress'} value={`${translations["in_progress"] || 'In progress'}: ${inProgress}`} /> : null}
        {error > 0 ? <StorageStatusCustomChip key='errorChipStatus' className={'chip_Error'} value={`${translations["error"] || "Error"} : ${error}`} /> : null}
        {warning > 0 ? <StorageStatusCustomChip key='warningChipStatus' className={'chip_Warning'} value={`${translations["warning"] || "Warning"}: ${warning}`} /> : null}
        {aborted > 0 ? <StorageStatusCustomChip key='abortedChipStatus' className={'chip_Aborted'} value={`${translations["aborted_by_user"] || "Aborted by user"} : ${aborted}`} /> : null}
        {started > 0 ? <StorageStatusCustomChip key='startedChipStatus' className={'chip_Started'} value={`${translations["started"] || "Started"} : ${started}`} /> : null}
        {!verified && !notVerified && !inProgress && !error && !warning && !aborted && !started ? <StorageStatusCustomChip key='noStoragesChipStatus' className={'chip_InProgress'} value={`${translations["no_storages"] || "No storages"}`} /> : null}

    </>
}